import React, { useEffect, useMemo, useState } from 'react';
import { Header, flagEmoji } from './Chrome';
import { SiteFooterV2 } from './Footers';
import { COUNTRY_SLUGS } from '../../lib/countrySlugs';

///////////////////////////////////////////////////////////////////////////////
// /countries — a directory of every country we serve, with a link to
// whichever of eSIM / mobile topup / gift cards is actually stocked there.
// "Actually stocked" is the point: COUNTRY_SLUGS carries ~230 entries (routes
// exist for all of them), but real catalog coverage is a subset, so this page
// cross-references the three product catalogs' own country-list endpoints
// rather than assuming every slug is sellable.
///////////////////////////////////////////////////////////////////////////////

// Countries under comprehensive/broad U.S. sanctions — excluded regardless of
// catalog data. Reviewed 2026-09-16; revisit if sanctions programs change.
const EXCLUDED_ISOS = new Set(['CU', 'RU', 'BY']);

function useCountryCapabilities() {
  const [caps, setCaps] = useState(null);

  useEffect(() => {
    let cancelled = false;
    Promise.all([
      fetch('/api/airtime/countries').then((r) => (r.ok ? r.json() : null)).catch(() => null),
      fetch('/api/esims/locations').then((r) => (r.ok ? r.json() : null)).catch(() => null),
      fetch('/api/gifts/countries').then((r) => (r.ok ? r.json() : null)).catch(() => null),
    ]).then(([airtime, esim, gifts]) => {
      if (cancelled) return;
      const topup = new Set((airtime && airtime.countries || []).map((c) => c.country_iso));
      // Thailand's topup runs on the dedicated ThailandTopupClient (the
      // original 2009 business), not the Ding/Reloadly/Zendit airtime
      // catalog this endpoint aggregates — add it back by hand.
      topup.add('TH');
      setCaps({
        topup,
        esim: new Set((esim && esim.locations || []).filter((l) => (l.plan_count || 0) > 0).map((l) => l.code)),
        gifts: new Set((gifts && gifts.countries || []).map((c) => c.iso)),
      });
    });
    return () => { cancelled = true; };
  }, []);

  return caps;
}

export default function CountriesPage({ me, currency, onCurrency }) {
  const caps = useCountryCapabilities();
  const [q, setQ] = useState('');

  const rows = useMemo(() => {
    const list = Object.entries(COUNTRY_SLUGS)
      .filter(([, c]) => !EXCLUDED_ISOS.has(c.iso))
      .map(([slug, c]) => ({ slug, iso: c.iso, name: c.name }))
      .sort((a, b) => a.name.localeCompare(b.name));
    if (!caps) return list.map((c) => ({ ...c, esim: null, topup: null, gifts: null }));
    return list
      .map((c) => ({
        ...c,
        esim: caps.esim.has(c.iso),
        topup: caps.topup.has(c.iso),
        gifts: caps.gifts.has(c.iso),
      }))
      .filter((c) => c.esim || c.topup || c.gifts);
  }, [caps]);

  const filtered = useMemo(() => {
    const f = q.trim().toLowerCase();
    if (!f) return rows;
    return rows.filter((c) => c.name.toLowerCase().includes(f));
  }, [rows, q]);

  return (
    <div className="page">
      <Header me={me} currency={currency} onCurrency={onCurrency} />

      <div className="countries-page">
        <h1 className="countries-h1">Supported Countries</h1>
        <p className="countries-intro">
          Every country we serve, and what’s available there — prepaid eSIM data, mobile airtime top-up, or
          gift cards. Pick a country to go straight to its page.
        </p>

        <input
          type="search" className="countries-search" placeholder="Search countries…"
          value={q} onChange={(e) => setQ(e.target.value)} aria-label="Search countries"
        />

        {!caps && <p className="countries-loading">Loading coverage…</p>}

        {caps && (
          <ul className="countries-list">
            {filtered.map((c) => (
              <li key={c.slug} className="countries-row">
                <span className="countries-flag" aria-hidden="true">
                  {/^[A-Z]{2}$/.test(c.iso) ? flagEmoji(c.iso) : '🏳️'}
                </span>
                <span className="countries-name">{c.name}</span>
                <span className="countries-links">
                  {c.esim && <a href={`/esim/${c.slug}`}>eSIM Data</a>}
                  {c.topup && <a href={`/topup/${c.slug}`}>Mobile Topup</a>}
                  {c.gifts && <a href={`/gifts/${c.slug}`}>Gift Cards</a>}
                </span>
              </li>
            ))}
            {filtered.length === 0 && <li className="countries-empty">No countries match “{q}”.</li>}
          </ul>
        )}
      </div>

      <SiteFooterV2 currency={currency} onCurrency={onCurrency} />
    </div>
  );
}
